import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { S25VirtualGridAbstractItem } from "../s25-virtual-grid/s25.virtual.grid.abstract.item";
import { ScheduleOnlyGrid } from "./s25.academic.grids.util";
import { Bind } from "../../decorators/bind.decorator";
import { Item } from "../../pojo/Item";
import { TooltipResponse } from "../../services/tooltip.service";

@Component({
    standalone: false,
    selector: "s25-ng-schedule-only-grid-item",
    template: `
        @if (item.isOverlapItem) {
            <div class="item overlapItem grid-color" data-type="overlapping"></div>
        } @else {
            <div
                class="item grid-color"
                [attr.data-type]="getGridColorType(item)"
                [attr.data-pattern]="getGridColorPattern(item)"
                [class.schedule]="item.data._isStandardSchedule"
                [class.nonStandardSchedule]="!item.data._fitsSchedule()"
                [class.shadow]="item.data._isShadow"
                [class.hasOverlap]="!!item.overlapping?.length"
                [class.dragging]="isDragging"
            >
                <s25-item-event
                    [modelBean]="{ itemId: item.data.eventId, itemName: item.data.eventName }"
                    [newTab]="true"
                    [empty]="true"
                    [dataService]="eventTooltipService"
                    [dataServiceContext]="item"
                    [contextMenuOnBody]="true"
                >
                    <div class="eventWrapper" (click)="$event.stopPropagation()">
                        @let bubbleUp = item._gridData.top <= 0.001 ? "down" : "up";
                        @let bubbleDown = item._gridData.top + item._gridData.height >= 99.999 ? "up" : "down";
                        @if (isDragging && (!!item.data.setup || !!item.data.pre)) {
                            <s25-ng-time-bubble [position]="bubbleDown" [hours]="item.candidate.startHour" />
                        }
                        @if (!!item.data.setup) {
                            <div class="additionalTime setup" [style.--part-hours]="item.data.setup / 60"></div>
                        }
                        @if (!!item.data.pre) {
                            <div class="additionalTime pre" [style.--part-hours]="item.data.pre / 60"></div>
                        }
                        @if (isDragging) {
                            <s25-ng-time-bubble [position]="bubbleUp" [hours]="item.candidate.eventStartHour" />
                        }
                        <div class="itemContent">
                            {{ item.data._isShadow ? item.data._realItem.data.dow : "" }}
                        </div>
                        @if (isDragging) {
                            <s25-ng-time-bubble [position]="bubbleDown" [hours]="item.candidate.eventEndHour" />
                        }
                        @if (!!item.data.post) {
                            <div class="additionalTime post" [style.--part-hours]="item.data.post / 60"></div>
                        }
                        @if (!!item.data.takedown) {
                            <div class="additionalTime takedown" [style.--part-hours]="item.data.takedown / 60"></div>
                        }
                        @if (isDragging && (!!item.data.post || !!item.data.takedown)) {
                            <s25-ng-time-bubble [position]="bubbleUp" [hours]="item.candidate.endHour" />
                        }
                    </div>
                </s25-item-event>
            </div>
        }

        <ng-template #overlappingItems let-item="item">
            <ul class="overlapInfo">
                <li>
                    <s25-ng-button [type]="'link'" (click)="bringToFront.emit(item)" [title]="'Bring To Front'">
                        {{ item.data.eventName }}
                    </s25-ng-button>
                </li>
                @for (overlapItem of item.overlapping; track overlapItem.id) {
                    <li>
                        <s25-ng-button
                            [type]="'link'"
                            (click)="bringToFront.emit(overlapItem)"
                            [title]="'Bring To Front'"
                        >
                            {{ overlapItem.data.eventName }}
                        </s25-ng-button>
                    </li>
                }
            </ul>
        </ng-template>
    `,
    styles: `
        .item {
            height: calc(100% - 3 * var(--border-width));
            translate: 0 var(--border-width);
            border: var(--grid-color--border, var(--item-border));
        }

        .item.overlapItem {
            z-index: 10; /* Has to be in front of items */
            position: relative;
            height: calc(100% - 3 * var(--border-width));
            margin: calc(0px - var(--grid-color--border-width));
            box-sizing: content-box;
        }

        .item.shadow {
            text-align: center;
            position: relative;
        }

        .item.hasOverlap .itemContent {
            text-align: left;
        }

        ::ng-deep s25-ng-schedule-only-grid-item .item .s25-item-holder {
            width: 100%;
        }

        ::ng-deep s25-ng-schedule-only-grid-item .item .ngInlineBlock,
        ::ng-deep s25-ng-schedule-only-grid-item .item .ngInlineBlock > div,
        ::ng-deep s25-ng-schedule-only-grid-item .item .s25-item-holder {
            height: 100%;
            display: block;
        }

        .eventWrapper {
            line-height: calc(var(--row-height) - 2 * var(--border-width));
            display: flex;
            height: 100%;
        }

        .eventWrapper > div:where(:first-of-type, :last-of-type) {
            flex-basis: calc(var(--column-width) * var(--part-hours) - var(--border-width));
        }

        .item .itemContent {
            padding-inline: 0.25rem;
            flex-grow: 1;
            flex-shrink: 0;
        }

        .additionalTime {
            background-color: rgb(255 255 255 / 40%);
            flex-shrink: 1;
            flex-grow: 0;
            flex-basis: calc(var(--column-width) * var(--part-hours));
        }

        .setup,
        .pre {
            border-inline-end: 1px dotted black;
        }

        .post,
        .takedown {
            border-inline-start: 1px dotted black;
        }

        .dragging {
            pointer-events: none;
        }

        .item:hover .dragMessage {
            display: flex;
        }

        .dragMessage {
            justify-content: center;
            display: none;

            --bubble-max-width: 30ch;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25ScheduleOnlyGridItemComponent
    extends S25VirtualGridAbstractItem<ScheduleOnlyGrid.ItemData>
    implements OnChanges
{
    @Input() isDragging: boolean;
    @Output() bringToFront = new EventEmitter<ScheduleOnlyGrid.Item>();

    @ViewChild("overlappingItems") overlappingItemsTemplate: TemplateRef<any>;

    dragUpdateInterval: ReturnType<typeof setInterval>;

    constructor(private cd: ChangeDetectorRef) {
        super();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.isDragging) {
            this.setDragUpdateInterval(!!changes.isDragging.currentValue);
        }
    }

    setDragUpdateInterval(state: boolean) {
        clearInterval(this.dragUpdateInterval);
        if (state) {
            let prev = this.item.candidate.startHour;
            this.dragUpdateInterval = setInterval(() => {
                if (prev !== this.item.candidate.startHour) {
                    ScheduleOnlyGrid.updateCandidateData([this.item as unknown as ScheduleOnlyGrid.Item]);
                    this.cd.detectChanges();
                    prev = this.item.candidate.startHour;
                }
            }, 100);
        }
    }

    getGridColorType(item: ScheduleOnlyGrid.Item): string {
        if (item.data._isStandardSchedule) return "standardSchedule";
        if (item.data._isShadow) return "blackout";
        if (item.data.adHoc) return "closed";
        if (!!item.data.locations.length) return "eventWithLocations";
        return "event";
    }

    getGridColorPattern(item: ScheduleOnlyGrid.Item): string {
        if (item.data._isStandardSchedule) return "none";
        if (!item.data._fitsSchedule()) return "conflict";
        if (item.data.instructorConflict()) return "instructorConflict";
        if (item.data.locationConflict()) return "locationConflict";
        if (item.data.resourceConflict()) return "resourceConflict";
        return "none";
    }

    @Bind
    async eventTooltipService(
        itemType: Item.Id,
        eventId: number,
        reservationId: number,
        item: ScheduleOnlyGrid.Item,
    ): Promise<TooltipResponse> {
        const template = this.overlappingItemsTemplate;
        return ScheduleOnlyGrid.eventTooltipService(template, itemType, eventId, reservationId, item);
    }
}
